import { getUserDisplayName } from 'shared-helpers/common';
import { useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getFormattedDate } from 'lib/common';
import { Badge } from 'components/common';
import { ProfileAvatar } from '.';

import type { RequestUser } from 'shared-types';

interface Props {
  isLimitedView?: boolean;
  isPreview: boolean;
  isUserPage: boolean;
  targetUser: RequestUser;
}

const ProfileSummaryHeader = ({
  isLimitedView = false,
  isPreview,
  isUserPage,
  targetUser
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages, serverConfig, theme } = useReduxState();

  return (
    <div className="info">
      <div className="avatar">
        <div className="user">
          <ProfileAvatar
            isPreview={isPreview}
            isUserPage={isUserPage}
            targetUser={targetUser}
          />
        </div>
        <div className="data">
          <div className="user-name">
            {getUserDisplayName({ messages, serverConfig, user: targetUser })}

            {targetUser?.roles.includes('superadmin') ? (
              <div className="user-badge">
                <Badge
                  category="dark"
                  size="small"
                  text={messages.usersSuperadmin}
                />
              </div>
            ) : targetUser?.roles.includes('admin') ? (
              <div className="user-badge">
                <Badge
                  category="dark"
                  size="small"
                  text={messages.usersAdmin}
                />
              </div>
            ) : null}
          </div>

          {!isLimitedView && (
            <div className="user-history">
              <div className="user-item">
                {`${messages.usersAccountCreated} ${getFormattedDate({
                  date: targetUser.createdAt
                })}`}
              </div>

              <div className="user-item">
                {messages.usersLastLogin}
                {targetUser.lastLogin
                  ? ` ${getFormattedDate({ date: targetUser.lastLogin })}`
                  : ' -'}
              </div>
            </div>
          )}

          {!isLimitedView && targetUser?.expirationDate ? (
            <div className="user-item danger-item">
              <Badge
                category={
                  Date.parse(targetUser.expirationDate as string) < dateNow
                    ? 'error-light'
                    : 'warning-light'
                }
                icon={['far', 'calendar-alt']}
                size="small"
                text={`${
                  Date.parse(targetUser.expirationDate as string) < dateNow
                    ? messages.usersAccountExpired
                    : messages.usersAccountExpires
                } ${getFormattedDate({ date: targetUser.expirationDate })}`}
              />
            </div>
          ) : null}

          {!isLimitedView && targetUser?.locked ? (
            <div className="user-item danger-item">
              <Badge
                category="error-light"
                icon="lock"
                size="small"
                text={messages.usersAccountLocked}
              />
            </div>
          ) : null}
        </div>
      </div>

      <style jsx>{`
        .avatar {
          align-items: center;
          display: flex;
        }
        .user {
          margin-right: 15px;
        }
        .user-name {
          color: ${theme.textColorLight};
          font-size: 2rem;
          font-weight: 700;
        }
        .user-badge {
          display: inline-block;
          margin-left: 10px;
          position: relative;
          top: -2px;
        }
        .user-item {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 600;
          margin-top: 3px;
        }
        .danger-item {
          margin-top: 5px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .user-history {
            display: flex;
            gap: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileSummaryHeader;
